const FLIGHT_PARTS = {
  target: ['target_base_fare', 'target_fuel_surcharge', 'target_taxes'],
  refund: ['supplier_refund_base_fare', 'supplier_refund_fuel_surcharge', 'supplier_refund_taxes'],
  additional: ['additional_base_fare', 'additional_fuel_surcharge', 'additional_taxes'],
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';

function hasNonZeroPart(item, group) {
  return (FLIGHT_PARTS[group] || []).some((key) => hasValue(item?.[key]) && num(item?.[key]) !== 0);
}

function hasRefundInput(item) {
  // "0.00" 같은 0 placeholder 는 환불 입력으로 간주 X (실제 nonzero 환불만 인식).
  return hasNonZeroPart(item, 'refund')
    || (hasValue(item?.supplier_refund) && num(item?.supplier_refund) !== 0)
    || (hasValue(item?.supplier_cancel_fee) && num(item?.supplier_cancel_fee) !== 0);
}

function hasAdditionalInput(item) {
  return hasNonZeroPart(item, 'additional') || num(item?.additional_cost) !== 0;
}

function sourceFlightState(flight = {}, index = 0) {
  const baseFare = num(flight.base_fare);
  const fuelSurcharge = num(flight.fuel_surcharge);
  const taxes = num(flight.taxes);
  const partsTotal = baseFare + fuelSurcharge + taxes;
  const total = num(flight.total_payment) || partsTotal;
  return {
    ...flight,
    id: flight.id || `current-flight-${index}`,
    base_fare: baseFare || (partsTotal ? 0 : total),
    fuel_surcharge: fuelSurcharge,
    taxes,
    total_payment: total,
  };
}

function sourceHotelStates(sourceHotels = []) {
  const rows = [];
  (sourceHotels || []).forEach((hotel, hotelIndex) => {
    const rates = Array.isArray(hotel?.nightly_rates) ? hotel.nightly_rates : [];
    if (rates.length) {
      rates.forEach((rate, rateIndex) => {
        rows.push({
          source_hotel_id: hotel.id,
          item_label: hotel.hotel_name || `호텔 ${hotelIndex + 1}`,
          source_night: Number(rate?.night || rateIndex + 1),
          target_cost: num(rate?.amount),
        });
      });
      return;
    }
    rows.push({
      source_hotel_id: hotel?.id,
      item_label: hotel?.hotel_name || `호텔 ${hotelIndex + 1}`,
      source_night: 1,
      target_cost: num(hotel?.total_cost),
    });
  });
  return rows.length ? rows : [{ item_label: '호텔', source_night: 1, target_cost: 0 }];
}

function labelIndex(label) {
  const match = String(label || '').match(/#\s*(\d+)/);
  return match ? Number(match[1]) - 1 : -1;
}

function hotelNightIndex(label) {
  const match = String(label || '').match(/(\d+)\s*박/);
  return match ? Number(match[1]) - 1 : -1;
}

function serviceIndex(item, fallbackIndex, type) {
  const byLabel = type === 'hotel' ? hotelNightIndex(item?.item_label) : labelIndex(item?.item_label);
  if (byLabel >= 0) return byLabel;

  const sortOrder = Number(item?.sort_order);
  if (Number.isFinite(sortOrder) && sortOrder >= 0) {
    if (type === 'hotel' && sortOrder >= 200) return sortOrder - 200;
    if (type === 'flight') return sortOrder;
  }
  return fallbackIndex;
}

function meaningfulServiceItem(item) {
  return hasRefundInput(item) || hasAdditionalInput(item);
}

function applyFlightItem(prior, item, index) {
  if (hasAdditionalInput(item)) {
    if (hasNonZeroPart(item, 'additional')) {
      const baseFare = num(item.additional_base_fare);
      const fuelSurcharge = num(item.additional_fuel_surcharge);
      const taxes = num(item.additional_taxes);
      return sourceFlightState({
        ...prior,
        base_fare: baseFare,
        fuel_surcharge: fuelSurcharge,
        taxes,
        total_payment: baseFare + fuelSurcharge + taxes,
      }, index);
    }
    const total = num(item.additional_cost);
    return sourceFlightState({ ...prior, total_payment: total, base_fare: total, fuel_surcharge: 0, taxes: 0 }, index);
  }

  if (hasRefundInput(item)) {
    return sourceFlightState({ ...prior, total_payment: 0, base_fare: 0, fuel_surcharge: 0, taxes: 0 }, index);
  }

  return prior;
}

function applyAggregateItem(priorAmount, item) {
  if (hasAdditionalInput(item)) return num(item.additional_cost);
  if (hasRefundInput(item)) return 0;
  return num(priorAmount);
}

function applyServiceItems(state, items, type) {
  const serviceItems = items
    .filter((item) => item?.item_type === type && meaningfulServiceItem(item))
    .sort((a, b) => num(a.sort_order) - num(b.sort_order));

  serviceItems.forEach((item, itemIndex) => {
    const index = serviceIndex(item, itemIndex, type);
    if (type === 'flight') {
      const prior = state.flights[index] || sourceFlightState({ total_payment: 0 }, index);
      state.flights[index] = applyFlightItem(prior, item, index);
      return;
    }
    if (type === 'hotel') {
      const prior = state.hotels[index] || { item_label: item.item_label || `호텔 ${index + 1}`, source_night: index + 1, target_cost: 0 };
      state.hotels[index] = { ...prior, target_cost: applyAggregateItem(prior.target_cost, item) };
      return;
    }
    if (type === 'car') {
      state.car_total = applyAggregateItem(state.car_total, item);
    }
  });
}

function toSourceHotels(rows) {
  return rows.filter(Boolean).map((row, index) => ({
    id: row.source_hotel_id || `current-hotel-${index}`,
    hotel_name: row.item_label || `호텔 ${index + 1}`,
    nightly_rates: [{ night: row.source_night || index + 1, amount: num(row.target_cost) }],
    total_cost: num(row.target_cost),
  }));
}

export function buildCurrentRevenueContext(events = [], baseline = {}, sourceFlights = [], sourceHotels = []) {
  const state = {
    customer_paid: num(baseline.customer_paid),
    car_total: num(baseline.car_total),
    current_profit: num(baseline.profit ?? baseline.summary_profit ?? baseline.difference),
    event_reflection_amount: 0,
    flights: (sourceFlights || []).map(sourceFlightState),
    hotels: sourceHotelStates(sourceHotels),
  };

  const ordered = [...(events || [])].sort((a, b) => {
    const left = `${a.event_date || ''}-${String(a.id || 0).padStart(10, '0')}`;
    const right = `${b.event_date || ''}-${String(b.id || 0).padStart(10, '0')}`;
    return left.localeCompare(right);
  });

  ordered.forEach((event) => {
    const isBaseEvent = (event.source === 'system' && event.source_key === 'booking')
      || (event.source === 'import' && event.source_key === 'import-current');
    if (isBaseEvent) return;

    const items = Array.isArray(event.items) ? event.items : [];
    state.event_reflection_amount += num(event.amount);
    items
      .filter((item) => item?.item_type === 'customer')
      .forEach((item) => {
        state.customer_paid += num(item.customer_additional_payment) - num(item.customer_refund);
      });
    applyServiceItems(state, items, 'flight');
    applyServiceItems(state, items, 'car');
    applyServiceItems(state, items, 'hotel');
  });

  const flightTotal = state.flights.reduce((sum, row) => sum + num(row.total_payment), 0);
  const hotelTotal = state.hotels.reduce((sum, row) => sum + num(row?.target_cost), 0);

  return {
    baseline: {
      ...baseline,
      customer_paid: state.customer_paid,
      flight_total: flightTotal,
      car_total: state.car_total,
      hotel_total: hotelTotal,
      current_profit: state.current_profit,
      event_reflection_amount: state.event_reflection_amount,
    },
    flights: state.flights,
    hotels: toSourceHotels(state.hotels),
  };
}
